/**
 * MapLogo — MAP brand logo component.
 *
 * Variants:
 *  - "full"    : pill mark + "MAP" wordmark + tagline     (login page)
 *  - "compact" : pill mark + "MAP" wordmark               (expanded sidebar)
 *  - "mark"    : pill mark only                           (collapsed sidebar, favicon)
 *
 * Themes:
 *  - "dark"  : dark pill on light background  (#18181b fill)
 *  - "light" : white pill on dark background  (#ffffff fill)
 *
 * The "M" inside the pill is always emerald (#10b981) regardless of theme.
 */

interface MapLogoProps {
  variant?: 'full' | 'compact' | 'mark';
  theme?: 'dark' | 'light';
  className?: string;
}

const PILL  = { dark: '#18181b', light: '#ffffff' } as const;
const TEXT  = { dark: '#18181b', light: '#ffffff' } as const;
const MUTED = { dark: '#71717a', light: '#a1a1aa' } as const;
const ACCENT = '#10b981'; // emerald-500 — always fixed

export function MapLogo({ variant = 'compact', theme = 'dark', className }: MapLogoProps) {
  const pillFill = PILL[theme];
  const textFill = TEXT[theme];
  const mutedFill = MUTED[theme];

  /* ── Mark only (32×32) ─────────────────────────────────────────────── */
  if (variant === 'mark') {
    return (
      <svg
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 32 32"
        fill="none"
        role="img"
        aria-label="MAP"
        className={className}
        width={32}
        height={32}
      >
        <rect width="32" height="32" rx="7" fill={pillFill} />
        <text
          x="16"
          y="23"
          fontFamily="Inter, system-ui, sans-serif"
          fontSize="18"
          fontWeight="700"
          textAnchor="middle"
          fill={ACCENT}
        >
          M
        </text>
      </svg>
    );
  }

  /* ── Compact: pill mark + "MAP" wordmark (120×28) ──────────────────── */
  if (variant === 'compact') {
    return (
      <svg
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 120 28"
        fill="none"
        role="img"
        aria-label="MAP"
        className={className}
        height={28}
      >
        {/* Pill mark */}
        <rect width="28" height="28" rx="6" fill={pillFill} />
        <text
          x="14"
          y="20.5"
          fontFamily="Inter, system-ui, sans-serif"
          fontSize="16"
          fontWeight="700"
          textAnchor="middle"
          fill={ACCENT}
        >
          M
        </text>

        {/* Wordmark */}
        <text
          x="38"
          y="20"
          fontFamily="Inter, system-ui, sans-serif"
          fontSize="15"
          fontWeight="700"
          letterSpacing="-0.4"
          fill={textFill}
        >
          MAP
        </text>
      </svg>
    );
  }

  /* ── Full: pill mark + wordmark + tagline (200×48) ─────────────────── */
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 200 48"
      fill="none"
      role="img"
      aria-label="MAP — Marketing Automation Platform"
      className={className}
      height={48}
    >
      {/* Pill mark */}
      <rect width="40" height="40" rx="9" y="4" fill={pillFill} />
      <text
        x="20"
        y="29"
        fontFamily="Inter, system-ui, sans-serif"
        fontSize="22"
        fontWeight="700"
        textAnchor="middle"
        fill={ACCENT}
      >
        M
      </text>

      {/* Wordmark */}
      <text
        x="52"
        y="32"
        fontFamily="Inter, system-ui, sans-serif"
        fontSize="21"
        fontWeight="700"
        letterSpacing="-0.5"
        fill={textFill}
      >
        MAP
      </text>

      {/* Tagline */}
      <text
        x="53"
        y="42"
        fontFamily="Inter, system-ui, sans-serif"
        fontSize="7.5"
        fontWeight="400"
        letterSpacing="1.8"
        fill={mutedFill}
      >
        AUTOMATION PLATFORM
      </text>
    </svg>
  );
}
